import React, { useEffect, useState, useRef, useMemo } from 'react';
import moment from 'moment';

// Hook for gradual number animation
const useAnimatedValue = (targetValue, duration = 800, enabled = true) => {
  const [displayValue, setDisplayValue] = useState(targetValue);
  const animationRef = useRef(null);
  const startValueRef = useRef(targetValue);
  const startTimeRef = useRef(null);
  const targetValueRef = useRef(targetValue);
  const isFirstRender = useRef(true);

  useEffect(() => {
    targetValueRef.current = targetValue;

    // On first render or if disabled, set immediately without animation
    if (isFirstRender.current || !enabled) {
      setDisplayValue(targetValue);
      isFirstRender.current = false;
      return;
    }

    if (targetValue === displayValue) return;

    // Cancel any ongoing animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    startValueRef.current = displayValue;
    startTimeRef.current = Date.now();

    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const current = Math.floor(startValueRef.current + (targetValueRef.current - startValueRef.current) * easeOut);

      setDisplayValue(current);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setDisplayValue(targetValueRef.current);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [targetValue, duration, enabled]);

  return displayValue;
};

/**
 * TimeTotalSelected - Calculates total duration only for entries in selectedEntries Set
 * This is used in CardHeaderImportDataCalendar to show only selected entries' total time
 * Updates immediately when checkboxes change (via selectedEntries state) without waiting for refetch
 * 
 * @param {Object} props - The props object containing item array and selectedEntries
 * @param {Array} props.item - Array where [0] is date and [1] is array of entries
 * @param {Array} props.item[1] - Array of entries, each with duration and _id properties
 * @param {Set} props.selectedEntries - Set of entry IDs that are currently selected
 * @returns {JSX.Element} Formatted time string in HH:mm:ss format with gradual animation
 */
const TimeTotalSelected = ({ item, selectedEntries = new Set() }) => {
  // Calculate total seconds from durations of selected entries only
  const totalSeconds = useMemo(() => {
    if (!item || !item[1] || !Array.isArray(item[1])) return 0;
    
    let tot = 0;
    item[1].forEach((entry) => {
      // Check if entry ID is in selectedEntries Set (immediate update, no refetch needed)
      if (entry && entry._id && selectedEntries.has(entry._id)) {
        tot = tot + (entry.duration || 0);
      }
    });
    return tot;
  }, [item, selectedEntries]);

  // Animate the total seconds value
  const animatedSeconds = useAnimatedValue(totalSeconds, 800, true);

  // Format the animated value as HH:mm:ss
  const formattedTime = moment.utc(animatedSeconds * 1000).format("HH:mm:ss");

  return <span>{formattedTime}</span>;
};

export default TimeTotalSelected;

